import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { Button } from '../buttons/button';
import { ICONS } from '../../constants/imageConstants';
import BaseModal from './BaseModal';

const SelectTeamMemberModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchMembers();
    }
  }, [isOpen]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(API_ENDPOINTS.chats.teamMembers);
      const membersData = res.data?.data || [];
      
      // Merge and deduplicate members using their unique ID
      const memberMap = new Map();
      if (Array.isArray(membersData)) {
        membersData.forEach((member) => {
          const id = member.uuid || member.id;
          if (id && !memberMap.has(id)) {
            memberMap.set(id, member);
          }
        });
      }

      const mergedMembers = Array.from(memberMap.values());
      setMembers(mergedMembers);
    } catch (error) {
      console.error('Failed to load team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!searchTerm.trim()) return members;
    const search = searchTerm.toLowerCase();
    return members.filter(member => {
      const name = (member.full_name || member.name || `${member.first_name || ''} ${member.last_name || ''}`).toLowerCase();
      const email = (member.email || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    });
  }, [members, searchTerm]);

  const handleContinue = () => {
    if (selectedMember) {
      onClose();
      const memberId = selectedMember.uuid || selectedMember.id;
      const memberName =
        selectedMember.full_name ||
        selectedMember.name ||
        `${selectedMember.first_name || ''} ${selectedMember.last_name || ''}`.trim() ||
        'Team Member';
      const memberAvatar = selectedMember.profile_image || selectedMember.avatar || null;
      navigate(`/chat/${memberId}`, { state: { name: memberName, avatar: memberAvatar } });
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="New Message"
      subtitle="Select a team member to start a conversation."
      icon={ICONS.USER_AVATAR}
      iconBgColor="#E0F2FE"
    >
      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search team members..."
            className="w-full h-11 pl-10 pr-4 bg-white border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-sm text-gray-500">Loading team members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-600">
                {searchTerm ? 'No team members found' : 'No team members available'}
              </p>
              {searchTerm && (
                <p className="text-xs text-gray-500 mt-1">Try adjusting your search</p>
              )}
            </div>
          ) : (
            filteredMembers.map((member) => {
              const memberId = member.uuid || member.id;
              const isSelected = !!selectedMember && (
                (selectedMember.uuid && selectedMember.uuid === member.uuid) ||
                (selectedMember.id && selectedMember.id === member.id)
              );
              
              return (
                <button
                  key={memberId}
                  onClick={() => setSelectedMember(member)}
                  className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="relative flex-shrink-0">
                      <img
                        src={member.profile_image || member.avatar || ICONS.USER_AVATAR}
                        alt={member.full_name || member.name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-100"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = ICONS.USER_AVATAR;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <h4 className={`text-sm font-semibold truncate transition-colors ${
                        isSelected ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {member.full_name || member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown Member'}
                      </h4>
                      <p className="text-xs text-gray-500 truncate">
                        {member.roles && member.roles.length > 0 
                          ? member.roles.map(r => r.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ') 
                          : (member.role || 'Team Member')}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <Button 
          variant="primary" 
          fullWidth 
          size="large" 
          onClick={handleContinue}
          disabled={!selectedMember}
        >
          Chat Now
        </Button>
      </div>
    </BaseModal>
  );
};

export default SelectTeamMemberModal;
